import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Circle, Square, RefreshCw } from 'lucide-react';
import ApiService from '../services/ApiService';
import {
  filterQAQuestions,
  groupByCategory,
  buildUploadFormData,
  getSupportedMimeType,
  pollForStoryQuestion,
  getFamilyUuids,
} from '../services/RecordingService';
import RecordingReview from './RecordingReview';

function RecordingView({ relative, mode, families, selectedFamily, currentUserUuid, calloutQuestion, onBack, onContentUploaded, onShowToast }) {
  // Camera state
  const videoPreviewRef = useRef(null);  // hidden video element for webcam
  const canvasRef = useRef(null);        // visible canvas for portrait preview
  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const animFrameRef = useRef(null);

  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef(null);

  // Question state (QA mode)
  const [categories, setCategories] = useState([]);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Story mode state
  const [storySessionUuid, setStorySessionUuid] = useState(null);
  const [storyQuestion, setStoryQuestion] = useState(null);
  const [storyLoading, setStoryLoading] = useState(false);
  const [sessionSummary, setSessionSummary] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);

  // Review state
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Answered UUIDs for filtering
  const answeredUuidsRef = useRef(new Set());

  // Start camera on mount
  useEffect(() => {
    startCamera();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reconnect stream to video element when returning from review
  useEffect(() => {
    if (!recordedBlob && mediaStreamRef.current && videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = mediaStreamRef.current;
      videoPreviewRef.current.onplaying = () => {
        startCanvasLoop();
      };
    }
  }, [recordedBlob, startCanvasLoop]);

  // Load questions/story based on mode
  useEffect(() => {
    if (mode === 'qa') loadQuestions();
    if (mode === 'story') initStorySession(false);
    if (mode === 'callout' && calloutQuestion) {
      // Callout comes pre-loaded
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Portrait canvas drawing loop — crops landscape webcam to 9:16 portrait
  const startCanvasLoop = useCallback(() => {
    const video = videoPreviewRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const draw = () => {
      if (video.readyState >= 2) {
        const vw = video.videoWidth;
        const vh = video.videoHeight;
        if (vw && vh) {
          // Target 9:16 portrait crop from center of landscape feed
          const targetRatio = 9 / 16;
          const cropWidth = Math.round(vh * targetRatio); // width based on full height
          const cropX = Math.round((vw - cropWidth) / 2);

          // Set canvas to portrait dimensions (match crop, no upscale)
          if (canvas.width !== cropWidth || canvas.height !== vh) {
            canvas.width = cropWidth;
            canvas.height = vh;
          }

          const ctx = canvas.getContext('2d');
          // Mirror horizontally for front camera
          ctx.save();
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(video, cropX, 0, cropWidth, vh, 0, 0, cropWidth, vh);
          ctx.restore();
        }
      }
      animFrameRef.current = requestAnimationFrame(draw);
    };
    draw();
  }, []);

  const stopCanvasLoop = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });
      mediaStreamRef.current = stream;
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.onplaying = () => {
          startCanvasLoop();
        };
      }
      setCameraReady(true);
      setCameraError(null);
    } catch (err) {
      console.error('Camera access failed:', err);
      setCameraError('Camera access denied. Please allow camera and microphone access in your browser settings.');
    }
  };

  const stopCamera = () => {
    stopCanvasLoop();
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const startRecording = () => {
    if (!canvasRef.current || !mediaStreamRef.current) return;
    chunksRef.current = [];

    // Capture portrait video from canvas + audio from original stream
    const canvasStream = canvasRef.current.captureStream(30);
    const audioTrack = mediaStreamRef.current.getAudioTracks()[0];
    if (audioTrack) canvasStream.addTrack(audioTrack);

    const mimeType = getSupportedMimeType();
    const recorder = new MediaRecorder(canvasStream, {
      ...(mimeType ? { mimeType } : {}),
      videoBitsPerSecond: 2_500_000,
    });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType || 'video/webm' });
      console.log(`Recording complete: ${chunksRef.current.length} chunks, ${(blob.size / 1024 / 1024).toFixed(2)} MB, type: ${blob.type}`);
      setRecordedBlob(blob);
    };
    recorder.start(1000);
    mediaRecorderRef.current = recorder;
    setIsRecording(true);
    setRecordingTime(0);
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // ── QA Mode ──

  const loadQuestions = async () => {
    setLoadingQuestions(true);
    try {
      const data = await ApiService.getQuestions();
      const allQ = data.questionsArray || [];

      // Use answeredQuestions from the relative object (already loaded)
      const answeredSet = new Set(relative.answeredQuestions || []);
      answeredUuidsRef.current = answeredSet;

      const filtered = filterQAQuestions(allQ, answeredSet);
      const grouped = groupByCategory(filtered);
      setCategories(grouped);
      setActiveCategoryIndex(0);
      setActiveQuestionIndex(0);
    } catch (err) {
      console.error('Failed to load questions:', err);
      onShowToast?.('Failed to load questions');
    } finally {
      setLoadingQuestions(false);
    }
  };

  // ── Story Mode ──

  const initStorySession = async (forceNew) => {
    setStoryLoading(true);
    try {
      const data = await ApiService.startStorySession(relative.uuid, forceNew);
      setStorySessionUuid(data.sessionUuid);
      setStoryQuestion({
        uuid: data.question.questionUuid,
        question: data.question.question,
        summary: data.question.summary,
        questionCategory: 'Story',
        questionScenario: ['Story'],
      });
      if (data.hasExistingSession && !forceNew) {
        setSessionSummary(data.sessionSummary || null);
        setShowResumeModal(true);
      } else {
        setShowResumeModal(false);
      }
    } catch (err) {
      console.error('Failed to start story session:', err);
      onShowToast?.('Failed to start story session');
    }
    setStoryLoading(false);
  };

  // ── Current question (across modes) ──

  const getCurrentQuestion = useCallback(() => {
    if (mode === 'qa') {
      if (categories.length === 0) return null;
      const [, catQuestions] = categories[activeCategoryIndex] || [];
      if (!catQuestions || catQuestions.length === 0) return null;
      return catQuestions[activeQuestionIndex] || catQuestions[0];
    }
    if (mode === 'story') return storyQuestion;
    if (mode === 'callout') return calloutQuestion;
    return null; // moment mode
  }, [mode, categories, activeCategoryIndex, activeQuestionIndex, storyQuestion, calloutQuestion]);

  // QA navigation
  const navigateQuestion = (direction) => {
    if (categories.length === 0) return;
    const [, catQuestions] = categories[activeCategoryIndex] || [];
    if (!catQuestions) return;

    let newQIdx = activeQuestionIndex + direction;
    let newCatIdx = activeCategoryIndex;

    if (newQIdx >= catQuestions.length) {
      // Move to next category
      newCatIdx = (activeCategoryIndex + 1) % categories.length;
      newQIdx = 0;
    } else if (newQIdx < 0) {
      // Move to previous category
      newCatIdx = (activeCategoryIndex - 1 + categories.length) % categories.length;
      const [, prevQ] = categories[newCatIdx] || [];
      newQIdx = prevQ ? prevQ.length - 1 : 0;
    }

    setActiveCategoryIndex(newCatIdx);
    setActiveQuestionIndex(newQIdx);
  };

  // ── Upload ──

  const handleSave = async (caption) => {
    const question = getCurrentQuestion();
    const familyUuids = getFamilyUuids(relative, families);
    setUploading(true);

    try {
      const formData = buildUploadFormData({
        videoBlob: recordedBlob,
        question,
        familyUuids,
        mode,
        caption,
        storySessionUuid: mode === 'story' ? storySessionUuid : undefined,
      });

      const uploadResult = await ApiService.uploadContent(formData);
      console.log('Upload response:', uploadResult);
      onShowToast?.('Memory uploaded!', 'success');
      onContentUploaded?.();

      if (mode === 'callout') {
        setRecordedBlob(null);
        onBack();
        return;
      }

      // Mark as answered for QA filtering
      if (question?.uuid) {
        answeredUuidsRef.current.add(question.uuid);
      }

      setRecordedBlob(null);

      if (mode === 'qa') {
        // Advance to next unanswered question
        advanceToNextQuestion();
      } else if (mode === 'story') {
        // Poll for AI follow-up question
        await pollNextStoryQuestion();
      }
      // moment mode: stays on camera for another recording
    } catch (err) {
      console.error('Upload failed:', err);
      onShowToast?.('Upload failed. Please try again.');
    }
    setUploading(false);
  };

  const advanceToNextQuestion = () => {
    if (categories.length === 0) return;
    // Re-filter questions excluding newly answered
    const allFiltered = [];
    categories.forEach(([cat, qs]) => {
      qs.forEach(q => {
        if (!answeredUuidsRef.current.has(q.uuid)) allFiltered.push(q);
      });
    });
    if (allFiltered.length === 0) {
      onShowToast?.('All questions answered!', 'success');
      return;
    }
    const grouped = groupByCategory(allFiltered);
    setCategories(grouped);
    setActiveCategoryIndex(0);
    setActiveQuestionIndex(0);
  };

  const pollNextStoryQuestion = async () => {
    setStoryLoading(true);
    try {
      const data = await pollForStoryQuestion(storySessionUuid);
      setStoryQuestion({
        uuid: data.questionUuid,
        question: data.question,
        summary: data.summary,
        questionCategory: 'Story',
        questionScenario: ['Story'],
      });
    } catch (err) {
      console.error('Story polling failed:', err);
      onShowToast?.('Could not get next question. Try "New Topic".');
    }
    setStoryLoading(false);
  };

  const handleRetake = () => {
    setRecordedBlob(null);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ── Review screen ──

  if (recordedBlob) {
    return (
      <RecordingReview
        videoBlob={recordedBlob}
        question={getCurrentQuestion()}
        mode={mode}
        onRetake={handleRetake}
        onSave={handleSave}
        uploading={uploading}
      />
    );
  }

  // ── Camera screen ──

  const question = getCurrentQuestion();
  const currentCategoryName = mode === 'qa' && categories[activeCategoryIndex] ? categories[activeCategoryIndex][0] : null;

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/70 to-transparent">
        <div className="flex items-center justify-between">
          <button onClick={onBack} className="p-2 hover:bg-white/20 rounded-full transition-colors">
            <ChevronLeft className="w-7 h-7 text-white" />
          </button>
          <div className="text-center">
            <p className="text-white font-semibold text-sm">{relative.name}</p>
            <p className="text-gray-300 text-xs">
              {mode === 'qa' && 'Q&A Game'}
              {mode === 'story' && 'Story Mode'}
              {mode === 'moment' && 'Quick Moment'}
              {mode === 'callout' && 'Weekly Callout'}
            </p>
          </div>
          <div className="w-11" /> {/* spacer */}
        </div>
      </div>

      {/* Off-screen video element — webcam source for canvas (must not be display:none) */}
      <video
        ref={videoPreviewRef}
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        autoPlay
        playsInline
        muted
      />

      {/* Camera preview — portrait-cropped canvas in fixed-ratio frame */}
      {cameraError ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <p className="text-white text-lg mb-4">{cameraError}</p>
            <button onClick={startCamera} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white">
              Retry
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-black overflow-hidden pb-48">
          <canvas
            ref={canvasRef}
            className="h-full max-w-full aspect-[9/16]"
          />
        </div>
      )}

      {/* Recording timer */}
      {isRecording && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 bg-red-600/80 px-3 py-1 rounded-full flex items-center gap-2">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
          <span className="text-white text-sm font-medium">{formatTime(recordingTime)}</span>
        </div>
      )}

      {/* QA Category pills */}
      {mode === 'qa' && categories.length > 0 && !isRecording && (
        <div className="absolute top-20 left-0 right-0 px-4">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map(([catName], idx) => (
              <button
                key={catName}
                onClick={() => { setActiveCategoryIndex(idx); setActiveQuestionIndex(0); }}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  idx === activeCategoryIndex
                    ? 'bg-purple-600 text-white'
                    : 'bg-white/15 text-gray-300 hover:bg-white/25'
                }`}
              >
                {catName.replace(/([A-Z])/g, ' $1').trim()}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Story resume modal */}
      {showResumeModal && (
        <div className="absolute inset-0 z-20 bg-black/80 flex items-center justify-center p-6">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-sm w-full">
            <h3 className="text-white font-bold text-lg mb-2">Continue Story?</h3>
            {sessionSummary && (
              <p className="text-gray-300 text-sm mb-4">{sessionSummary}</p>
            )}
            <p className="text-gray-400 text-sm mb-6">You have an existing story session. Would you like to continue or start fresh?</p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowResumeModal(false); initStorySession(true); }}
                className="flex-1 py-2.5 rounded-xl bg-gray-700 hover:bg-gray-600 text-white font-medium text-sm transition-colors"
              >
                Start Fresh
              </button>
              <button
                onClick={() => setShowResumeModal(false)}
                className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-medium text-sm transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Story loading indicator */}
      {storyLoading && (
        <div className="absolute inset-0 z-20 bg-black/60 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-400 mx-auto mb-3" />
            <p className="text-white text-sm">Generating next question...</p>
          </div>
        </div>
      )}

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pb-8">
        {/* Question text */}
        {question && !isRecording && (
          <div className="mb-4 px-2">
            {currentCategoryName && (
              <p className="text-purple-400 text-xs font-medium mb-1">
                {currentCategoryName.replace(/([A-Z])/g, ' $1').trim()}
              </p>
            )}
            <p className="text-white font-semibold text-lg leading-snug">{question.question}</p>
          </div>
        )}

        {mode === 'moment' && !isRecording && (
          <div className="mb-4 px-2">
            <p className="text-white font-semibold text-lg">Capture a moment</p>
            <p className="text-gray-400 text-sm">You can add a caption after recording</p>
          </div>
        )}

        {/* QA loading */}
        {mode === 'qa' && loadingQuestions && (
          <div className="mb-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2" />
            <p className="text-gray-300 text-sm">Loading questions...</p>
          </div>
        )}

        {/* Controls row */}
        <div className="flex items-center justify-center gap-8">
          {/* Previous question (QA only) */}
          {mode === 'qa' && !isRecording ? (
            <button
              onClick={() => navigateQuestion(-1)}
              className="p-3 bg-white/15 hover:bg-white/25 rounded-full transition-colors"
              disabled={categories.length === 0}
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
          ) : (
            <div className="w-12" />
          )}

          {/* Record button */}
          {cameraReady && (
            <button
              onClick={isRecording ? stopRecording : startRecording}
              className="relative"
              disabled={storyLoading || loadingQuestions}
            >
              {isRecording ? (
                <div className="w-20 h-20 rounded-full border-4 border-red-500 flex items-center justify-center bg-black/30">
                  <Square className="w-8 h-8 text-red-500 fill-red-500" />
                </div>
              ) : (
                <div className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center">
                  <Circle className="w-16 h-16 text-red-500 fill-red-500" />
                </div>
              )}
            </button>
          )}

          {/* Next question (QA) / New Topic (Story) */}
          {mode === 'qa' && !isRecording ? (
            <button
              onClick={() => navigateQuestion(1)}
              className="p-3 bg-white/15 hover:bg-white/25 rounded-full transition-colors"
              disabled={categories.length === 0}
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          ) : mode === 'story' && !isRecording ? (
            <button
              onClick={() => initStorySession(true)}
              className="p-3 bg-white/15 hover:bg-white/25 rounded-full transition-colors"
              title="New Topic"
            >
              <RefreshCw className="w-6 h-6 text-white" />
            </button>
          ) : (
            <div className="w-12" />
          )}
        </div>
      </div>
    </div>
  );
}

export default RecordingView;
